import { useEffect, useState, type ReactNode } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Modal } from '@/components/common/AppModal';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import SubtleBadge from '@/components/common/SubtleBadge';
import { navigateToIndexWithPrefill, navigateToInspectWithPrefill } from '../lib/url-list-prefill';
import { getIndexerErrorInfo } from '../api/errors';
import { useLazyGetInspectTimelineQuery } from '../api/indexerApi';
import type { InspectTimelineData, InspectTimelineEntry } from '../api/types';
import { IndexerIcon } from './IndexerIcon';
import {
  bucketLabel,
  compareCrawlAndLastmod,
  crawlAllowedLabel,
  crawledAsLabel,
  crawlVsLastmodLabel,
  fetchLabel,
  formatInspectInstant,
  indexingAllowedLabel,
  inspectCoverageBucket,
} from '../lib/inspect-detail';
import { diffTimelineEntries } from '../lib/inspect-timeline-diff';
import {
  buildIndexGapInsight,
  type IndexGapInsight,
} from '../lib/inspect-index-gap';
import { formatRelativeFa, formatTime, toUtcIso } from '../lib/datetime';
import { HISTORY_WINDOW_LABEL } from '../lib/job-range';
import { copyText, decodeUrlForDisplay, formatFaNumber } from '../lib/utils';
import { getCoverageGuide } from '../lib/coverage-guides';
import { timelineEntryToSnapshot } from '../lib/seo-ai-prompt';
import { ActionTip } from './ActionTip';
import { CoverageGuideAccordion, CoverageHelpTip } from './CoverageHelpTip';
import { SearchConsoleIcon } from './SearchConsoleIcon';
import { SeoAiAdviceLink } from './SeoAiAdvice';

function bucketSubtleTone(
  bucket: ReturnType<typeof inspectCoverageBucket>,
): 'success' | 'warning' | 'danger' {
  if (bucket === 'indexed') return 'success';
  if (bucket === 'error') return 'danger';
  return 'warning';
}

function inspectSourceLabel(entry: InspectTimelineEntry): string {
  const source = entry.inspectSource;
  if (!source) return '—';
  if (source.trigger === 'schedule') {
    return source.scheduleName
      ? `زمان‌بندی — ${source.scheduleName}`
      : 'زمان‌بندی';
  }
  return 'دستی';
}

function KvRow({
  label,
  children,
  tone,
}: {
  label: string;
  children: ReactNode;
  tone?: 'success' | 'warning' | 'danger';
}) {
  return (
    <div
      className={classNames(
        'inspect-kv-row py-2',
        tone === 'success' && 'bg-success-subtle rounded px-2',
        tone === 'warning' && 'bg-warning-subtle rounded px-2',
        tone === 'danger' && 'bg-danger-subtle rounded px-2',
      )}
    >
      <dt className="inspect-kv-key text-secondary">{label}</dt>
      <dd className="inspect-kv-val mb-0">{children}</dd>
    </div>
  );
}

function InspectUrlLink({
  url,
  onRequestIndex,
  onInspectStatus,
}: {
  url: string;
  onRequestIndex?: (targetUrl: string) => void;
  onInspectStatus?: (targetUrl: string) => void;
}) {
  return (
    <div className="inspect-url-link" dir="ltr">
      <span className="inspect-url-link-text mono text-break">
        {decodeUrlForDisplay(url)}
      </span>
      <span className="inspect-url-link-actions">
        <ActionTip tip="کپی">
          <button
            type="button"
            className="inspect-url-link-btn"
            aria-label="کپی آدرس"
            onClick={() => void copyText(decodeUrlForDisplay(url))}
          >
            <IndexerIcon name="copy" />
          </button>
        </ActionTip>
        <ActionTip tip="باز کردن در تب جدید">
          <a
            className="inspect-url-link-btn"
            href={url}
            target="_blank"
            rel="noreferrer"
            aria-label="باز کردن در تب جدید"
          >
            <IndexerIcon name="box-arrow-up-right" />
          </a>
        </ActionTip>
        {onRequestIndex && (
          <ActionTip tip="درخواست ایندکس">
            <button
              type="button"
              className="inspect-url-link-btn"
              aria-label="درخواست ایندکس"
              onClick={() => onRequestIndex(url)}
            >
              <IndexerIcon name="card-text" />
            </button>
          </ActionTip>
        )}
        {onInspectStatus && (
          <ActionTip tip="وضعیت ایندکس">
            <button
              type="button"
              className="inspect-url-link-btn"
              aria-label="وضعیت ایندکس"
              onClick={() => onInspectStatus(url)}
            >
              <IndexerIcon name="search" />
            </button>
          </ActionTip>
        )}
      </span>
    </div>
  );
}

function UrlListValue({
  urls,
  onRequestIndex,
  onInspectStatus,
}: {
  urls: string[];
  onRequestIndex?: (targetUrl: string) => void;
  onInspectStatus?: (targetUrl: string) => void;
}) {
  if (urls.length === 0) return <>{'—'}</>;
  return (
    <ul className="inspect-kv-url-list">
      {urls.map((item) => (
        <li key={item}>
          <InspectUrlLink
            url={item}
            onRequestIndex={onRequestIndex}
            onInspectStatus={onInspectStatus}
          />
        </li>
      ))}
    </ul>
  );
}

function DiscoverySection({
  entry,
  onRequestIndex,
  onInspectStatus,
}: {
  entry: InspectTimelineEntry;
  onRequestIndex?: (targetUrl: string) => void;
  onInspectStatus?: (targetUrl: string) => void;
}) {
  const referringUrls = entry.referringUrls ?? [];
  const sitemaps = entry.sitemaps ?? [];

  return (
    <dl className="inspect-kv-list">
      <KvRow label="Sitemapها">
        <UrlListValue
          urls={sitemaps}
          onRequestIndex={onRequestIndex}
          onInspectStatus={onInspectStatus}
        />
      </KvRow>
      <KvRow label="صفحه ارجاع‌دهنده">
        <UrlListValue
          urls={referringUrls}
          onRequestIndex={onRequestIndex}
          onInspectStatus={onInspectStatus}
        />
      </KvRow>
      <KvRow label="lastmod">
        <span className="inspect-datetime">
          {formatInspectInstant(entry.sitemapLastmod ?? undefined)}
        </span>
      </KvRow>
    </dl>
  );
}

function CrawlSection({ entry }: { entry: InspectTimelineEntry }) {
  const crawlVs = compareCrawlAndLastmod(
    entry.lastCrawlTime ?? undefined,
    entry.sitemapLastmod ?? undefined,
  );
  const crawlHint = crawlVsLastmodLabel(crawlVs);

  return (
    <dl className="inspect-kv-list">
      <KvRow label="تاریخ خزش گوگل">
        <span className="inspect-datetime">
          {formatInspectInstant(entry.lastCrawlTime ?? undefined)}
        </span>
      </KvRow>
      <KvRow label="خزش‌شده به‌صورت">
        {crawledAsLabel(entry.crawledAs ?? undefined)}
      </KvRow>
      <KvRow label="اجازه خزش؟">
        {crawlAllowedLabel(entry.robotsTxtState ?? undefined)}
      </KvRow>
      <KvRow label="واکشی صفحه">
        {fetchLabel(entry.pageFetchState ?? undefined)}
      </KvRow>
      <KvRow label="اجازه ایندکس؟">
        {indexingAllowedLabel(entry.indexingState ?? undefined)}
      </KvRow>
      {crawlHint && (
        <KvRow label="مقایسه با lastmod">
          <span
            className={crawlVs === 'lastmod_newer' ? 'text-warning' : undefined}
          >
            {crawlHint}
          </span>
        </KvRow>
      )}
    </dl>
  );
}

function IndexSection({
  entry,
  onRequestIndex,
  onInspectStatus,
}: {
  entry: InspectTimelineEntry;
  onRequestIndex?: (targetUrl: string) => void;
  onInspectStatus?: (targetUrl: string) => void;
}) {
  return (
    <dl className="inspect-kv-list">
      <KvRow label="canonical کاربر">
        {entry.userCanonical ? (
          <InspectUrlLink
            url={entry.userCanonical}
            onRequestIndex={onRequestIndex}
            onInspectStatus={onInspectStatus}
          />
        ) : (
          '—'
        )}
      </KvRow>
      <KvRow label="canonical گوگل">
        {entry.googleCanonical ? (
          <InspectUrlLink
            url={entry.googleCanonical}
            onRequestIndex={onRequestIndex}
            onInspectStatus={onInspectStatus}
          />
        ) : (
          '—'
        )}
      </KvRow>
    </dl>
  );
}

function IndexGapAccordion({ insight }: { insight: IndexGapInsight }) {
  return (
    <Accordion className="onboarding-setup-guide-accordion">
      <Accordion.Item eventKey="0" className="border rounded-3 overflow-hidden">
        <Accordion.Header>
          <span className="d-flex flex-column align-items-start gap-1 flex-grow-1 text-start min-w-0">
            <SubtleBadge
              bg={insight.tone}
              pill
              className="fs--2"
              style={{ lineHeight: 1.35 }}
            >
              {insight.badge}
            </SubtleBadge>
            <span className="fs--1 fw-semibold">{insight.headline}</span>
          </span>
        </Accordion.Header>
        <Accordion.Body className="border-top pt-2 pb-3">
          {insight.details.length > 0 && (
            <ul className="mb-0 ps-3 fs--1 text-secondary">
              {insight.details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

function OurActionsSection({ entry }: { entry: InspectTimelineEntry }) {
  const indexReq = entry.lastIndexRequest;
  const error = entry.error;
  const hasIndex = !!indexReq;
  const hasError =
    !!error &&
    (!!error.message || error.httpStatus != null || error.guideStepsFa.length > 0);
  const gapInsight = buildIndexGapInsight(entry);
  const coverageGuide = getCoverageGuide(entry.coverageState);
  const hasAnyAction =
    !!entry.inspectSource || hasIndex || hasError || !!coverageGuide;

  return (
    <dl className="inspect-kv-list">
      {entry.inspectSource && (
        <KvRow label="منبع درخواست">{inspectSourceLabel(entry)}</KvRow>
      )}
      {indexReq && (
        <>
          <KvRow label="آخرین درخواست ایندکس">
            <span className="inspect-datetime">
              {formatInspectInstant(indexReq.requestedAt)}
            </span>
          </KvRow>
          <KvRow label="نتیجه درخواست">
            {indexReq.success ? 'موفق' : 'ناموفق'}
          </KvRow>
          {indexReq.notifyTime && (
            <KvRow label="اعلان گوگل (notifyTime)">
              <span className="inspect-datetime">
                {formatInspectInstant(indexReq.notifyTime)}
              </span>
            </KvRow>
          )}
          {gapInsight && (
            <KvRow label="فاصله از درخواست">
              <IndexGapAccordion insight={gapInsight} />
            </KvRow>
          )}
        </>
      )}
      {hasError && (
        <>
          {error.message && (
            <KvRow label="پیام خطا">
              <span className="text-danger">
                {error.message}
              </span>
            </KvRow>
          )}
          {error.httpStatus != null && (
            <KvRow label="HTTP">
              <span dir="ltr">{error.httpStatus}</span>
            </KvRow>
          )}
          {error.guideStepsFa.length > 0 && (
            <KvRow label="راهنما">
              <ol className="inspect-kv-guide-list">
                {error.guideStepsFa.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </KvRow>
          )}
        </>
      )}
      {coverageGuide && (
        <KvRow label="راهنمای ایندکس">
          <CoverageGuideAccordion guide={coverageGuide} />
        </KvRow>
      )}
      {!hasAnyAction && <KvRow label="—">اطلاعاتی ثبت نشده</KvRow>}
    </dl>
  );
}

function hasOurActions(entry: InspectTimelineEntry): boolean {
  const error = entry.error;
  const hasError =
    !!error &&
    (!!error.message || error.httpStatus != null || error.guideStepsFa.length > 0);
  return (
    !!entry.inspectSource ||
    !!entry.lastIndexRequest ||
    hasError ||
    !!getCoverageGuide(entry.coverageState)
  );
}

function ChangesSection({
  older,
  entry,
}: {
  older: InspectTimelineEntry;
  entry: InspectTimelineEntry;
}) {
  const changes = diffTimelineEntries(older, entry);
  if (changes.length === 0) return null;

  return (
    <div className="inspect-timeline-change">
      {changes.map((change) => (
        <div key={change.label} className="inspect-timeline-diff-block">
          <div className="inspect-timeline-diff-field">{change.label}</div>
          <div className="inspect-timeline-change-row">
            <span className="inspect-timeline-change-key">قبلاً</span>
            <span className="inspect-timeline-change-val inspect-timeline-change-val--mono">
              {change.before}
            </span>
          </div>
          <div className="inspect-timeline-change-row">
            <span className="inspect-timeline-change-key">اکنون</span>
            <span className="inspect-timeline-change-val inspect-timeline-change-val--mono">
              {change.after}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineEntryBody({
  entry,
  older,
  history,
  pageUrl,
  onRequestIndex,
  onInspectStatus,
}: {
  entry: InspectTimelineEntry;
  older?: InspectTimelineEntry;
  history: InspectTimelineEntry[];
  pageUrl: string;
  onRequestIndex: (targetUrl: string) => void;
  onInspectStatus: (targetUrl: string) => void;
}) {
  const changes = older ? diffTimelineEntries(older, entry) : [];
  const bucket = inspectCoverageBucket(
    entry.success,
    entry.verdict ?? undefined,
    entry.coverageState ?? undefined,
  );

  return (
    <div className="inspect-kv">
      <div className="inspect-kv-block">
        <h4 className="fs--1 fw-semibold text-secondary mb-2">کشف</h4>
        <DiscoverySection
          entry={entry}
          onRequestIndex={onRequestIndex}
          onInspectStatus={onInspectStatus}
        />
      </div>

      <div className="inspect-kv-block border-top pt-3">
        <h4 className="fs--1 fw-semibold text-secondary mb-2">خزش گوگل</h4>
        <CrawlSection entry={entry} />
      </div>

      <div className="inspect-kv-block border-top pt-3">
        <h4 className="fs--1 fw-semibold text-secondary mb-2">ایندکس</h4>
        <IndexSection
          entry={entry}
          onRequestIndex={onRequestIndex}
          onInspectStatus={onInspectStatus}
        />
      </div>

      {hasOurActions(entry) && (
        <div className="inspect-kv-block border-top pt-3">
          <h4 className="fs--1 fw-semibold text-secondary mb-2">اقدامات ما</h4>
          <OurActionsSection entry={entry} />
        </div>
      )}

      {changes.length > 0 && older && (
        <div className="inspect-kv-block border-top pt-3">
          <h4 className="fs--1 fw-semibold text-secondary mb-2">
            تغییر نسبت به درخواست قبلی
          </h4>
          <ChangesSection older={older} entry={entry} />
        </div>
      )}

      <div className="inspect-kv-footer">
        <SeoAiAdviceLink
          url={pageUrl}
          snapshots={history.map(timelineEntryToSnapshot)}
          disabled={bucket === 'indexed'}
        />
        {entry.inspectLink && (
          <a href={entry.inspectLink} target="_blank" rel="noreferrer">
            <SearchConsoleIcon className="inspect-kv-footer-icon" />
            باز کردن در Search Console
          </a>
        )}
        <button
          type="button"
          className="inspect-kv-footer-btn"
          onClick={() => onRequestIndex(pageUrl)}
        >
          <IndexerIcon name="card-text" />
          درخواست ایندکس
        </button>
        <button
          type="button"
          className="inspect-kv-footer-btn"
          onClick={() => onInspectStatus(pageUrl)}
        >
          <IndexerIcon name="search" />
          وضعیت ایندکس
        </button>
      </div>
    </div>
  );
}

function TimelineEntryHeader({ entry }: { entry: InspectTimelineEntry }) {
  const bucket = inspectCoverageBucket(
    entry.success,
    entry.verdict ?? undefined,
    entry.coverageState ?? undefined,
  );
  const createdIso = toUtcIso(entry.createdAt) ?? undefined;
  const absolute = formatTime(entry.createdAt);

  return (
    <div className="inspect-timeline-heading">
      <SubtleBadge
        bg={bucketSubtleTone(bucket)}
        pill
        className="fs--2"
        style={{ lineHeight: 1.35 }}
      >
        {bucketLabel(bucket)}
      </SubtleBadge>
      <div className="inspect-timeline-status">
        <span className="text-truncate" title={entry.coverageState ?? undefined}>
          {entry.coverageState || '—'}
        </span>
        <CoverageHelpTip coverageState={entry.coverageState} placement="bottom" />
      </div>
      <time
        className="inspect-timeline-time"
        dateTime={createdIso}
        title={createdIso ? `${createdIso} (UTC)` : undefined}
      >
        {formatRelativeFa(entry.createdAt)}
        {absolute ? (
          <span className="text-secondary"> ({absolute})</span>
        ) : null}
      </time>
    </div>
  );
}

export function UrlInspectHistory({
  projectId,
  url,
  show,
  onHide,
}: {
  projectId: string;
  url: string;
  show: boolean;
  onHide: () => void;
}) {
  const navigate = useNavigate();
  const [data, setData] = useState<InspectTimelineData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null);
  const [getInspectTimeline] = useLazyGetInspectTimelineQuery();

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setActiveEntryId(null);
    void getInspectTimeline({ projectId, url }, true)
      .unwrap()
      .then((next) => {
        if (!cancelled) {
          setData(next);
          setActiveEntryId(next.entries[0]?.id ?? null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setActiveEntryId(null);
        setError(
          getIndexerErrorInfo(err).message || 'خواندن تاریخچه ناموفق بود',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, projectId, url, getInspectTimeline]);

  function requestIndexAgain(targetUrl?: string) {
    const next = (targetUrl ?? url).trim() || url;
    onHide();
    navigateToIndexWithPrefill(navigate, [next], 'inspect-history');
  }

  function openInspectStatus(targetUrl?: string) {
    const next = (targetUrl ?? url).trim() || url;
    onHide();
    navigateToInspectWithPrefill(navigate, [next], 'inspect-history');
  }

  const decoded = decodeUrlForDisplay(url);

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable centered>
      <Modal.Header closeButton className="bg-body-tertiary border-bottom">
        <Modal.Title as="div" className="fs--1 fw-semibold mb-0 lh-sm">
          <div>تاریخچه ایندکس</div>
          <div className="text-secondary fs--2 fw-normal mt-1">
            {HISTORY_WINDOW_LABEL}
            {data && data.entries.length > 0
              ? ` · ${formatFaNumber(data.entries.length)} بازرسی`
              : null}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          className="d-flex align-items-center gap-2 border rounded-3 px-3 py-2 mb-3 bg-body"
          title={decoded}
          dir="ltr"
        >
          <span className="mono text-break text-primary fs--1">{decoded}</span>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => requestIndexAgain()}
          >
            درخواست ایندکس
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => openInspectStatus()}
          >
            بازرسی وضعیت
          </button>
        </div>

        {loading && (
          <div className="text-secondary py-5 text-center">
            <Spinner
              size="sm"
              animation="border"
              className="me-2"
              style={{ width: 12, height: 12 }}
            />
            در حال بارگذاری…
          </div>
        )}

        {error && <div className="text-danger fs--1 mb-3">{error}</div>}

        {!loading && data && data.entries.length === 0 && (
          <div className="text-secondary text-center py-5 border border-dashed rounded-3">
            در {HISTORY_WINDOW_LABEL} درخواستی برای این آدرس نیست.
          </div>
        )}

        {!loading && data && data.entries.length > 0 && (
          <Accordion
            activeKey={activeEntryId ?? undefined}
            onSelect={(key) => {
              setActiveEntryId(typeof key === 'string' ? key : null);
            }}
            className="inspect-timeline-accordion"
          >
            {data.entries.map((entry, index) => {
              const older = data.entries[index + 1];
              return (
                <Accordion.Item eventKey={entry.id} key={entry.id}>
                  <Accordion.Header>
                    <TimelineEntryHeader entry={entry} />
                  </Accordion.Header>
                  <Accordion.Body>
                    <TimelineEntryBody
                      entry={entry}
                      older={older}
                      history={data.entries}
                      pageUrl={url}
                      onRequestIndex={requestIndexAgain}
                      onInspectStatus={openInspectStatus}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Modal.Body>
    </Modal>
  );
}
